// src/components/AdminLoginPage.js
import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Link,
  Alert,
  CircularProgress,
  Fade,
  Stack,
  Divider,
  Avatar,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LoginIcon from "@mui/icons-material/Login";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";

const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/humbertack-workflow.firebasestorage.app/o/Minimalist%20HumberTrack%20Logo%20Design.png?alt=media&token=7fac08dd-27a3-466a-8172-48d50a827959";

function AdminLoginPage({ onBack }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email is invalid";
    }
    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Success - Firebase will trigger auth state change
    } catch (err) {
      console.error(err);
      let errorMessage = "Login failed. Please try again.";

      switch (err.code) {
        case "auth/user-not-found":
          errorMessage = "No account found with this email address.";
          break;
        case "auth/wrong-password":
          errorMessage = "Incorrect password. Please try again.";
          break;
        case "auth/invalid-email":
          errorMessage = "Invalid email address format.";
          break;
        case "auth/user-disabled":
          errorMessage = "This account has been disabled.";
          break;
        case "auth/too-many-requests":
          errorMessage = "Too many failed attempts. Please try again later.";
          break;
        default:
          errorMessage = err.message || "Login failed";
      }

      setMessage({ type: "error", text: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setMessage({
      type: "info",
      text: "Password reset coming soon. Please contact your administrator.",
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, #1d4ed8 0%, #020617 45%, #020617 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated background */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          background:
            "radial-gradient(circle at 20% 50%, #0ea5e9 0%, transparent 50%), radial-gradient(circle at 80% 80%, #4f46e5 0%, transparent 50%)",
          animation: "pulse 8s ease-in-out infinite",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 0.1 },
            "50%": { opacity: 0.2 },
          },
        }}
      />

      {/* Back button */}
      {onBack && (
        <IconButton
          onClick={onBack}
          sx={{
            position: 'absolute',
            top: 24,
            left: 24,
            color: 'white',
            bgcolor: 'rgba(15,23,42,0.8)',
            '&:hover': {
              bgcolor: 'rgba(15,23,42,0.95)',
            },
            zIndex: 10,
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      )}

      <Fade in timeout={800}>
        <Paper
          elevation={10}
          sx={{
            maxWidth: 440,
            width: "100%",
            p: 4,
            borderRadius: 4,
            background:
              "linear-gradient(145deg, rgba(15,23,42,0.95), rgba(15,23,42,0.9))",
            boxShadow:
              "0 20px 60px rgba(15,23,42,0.9), 0 0 0 1px rgba(59,130,246,0.4)",
            backdropFilter: "blur(10px)",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Logo and Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2,
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                boxShadow: '0 8px 24px rgba(59,130,246,0.4)',
              }}
            >
              <AdminPanelSettingsIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 0.5,
                background: "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Admin Portal
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              HumberTrack Workflow Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to manage jobs, engineers & clients
            </Typography>
          </Box>

          {/* Error/Info Message */}
          {message && (
            <Fade in>
              <Alert
                severity={message.type}
                sx={{ mb: 3 }}
                onClose={() => setMessage(null)}
              >
                {message.text}
              </Alert>
            </Fade>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleLogin} noValidate>
            <TextField
              label="Admin Email"
              type="email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: null });
              }}
              error={!!errors.email}
              helperText={errors.email}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "rgba(15,23,42,0.5)",
                  "&:hover": {
                    bgcolor: "rgba(15,23,42,0.7)",
                  },
                  "&.Mui-focused": {
                    bgcolor: "rgba(15,23,42,0.8)",
                  },
                },
              }}
            />

            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) setErrors({ ...errors, password: null });
              }}
              error={!!errors.password}
              helperText={errors.password}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "rgba(15,23,42,0.5)",
                  "&:hover": {
                    bgcolor: "rgba(15,23,42,0.7)",
                  },
                  "&.Mui-focused": {
                    bgcolor: "rgba(15,23,42,0.8)",
                  },
                },
              }}
            />

            {/* Remember Me & Forgot Password */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 1, mb: 2 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Remember me
                  </Typography>
                }
              />
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handleForgotPassword}
                disabled={isLoading}
                sx={{
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Forgot password?
              </Link>
            </Stack>

            {/* Sign In Button */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <LoginIcon />
                )
              }
              sx={{
                mt: 2,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                background:
                  "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)",
                boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)",
                  boxShadow: "0 6px 20px rgba(59, 130, 246, 0.6)",
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0px)",
                },
                "&:disabled": {
                  background: "rgba(100, 116, 139, 0.5)",
                  color: "rgba(255, 255, 255, 0.5)",
                },
              }}
            >
              {isLoading ? "Signing in..." : "Sign In as Admin"}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Secure Admin Access
            </Typography>
          </Divider>

          {/* Footer */}
          <Stack spacing={1} sx={{ textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              HumberTrack • Vehicle Technology Installations
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Need help? Contact your system administrator
            </Typography>
          </Stack>
        </Paper>
      </Fade>
    </Box>
  );
}

export default AdminLoginPage;